import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import AnaliseDeProducao from '@/components/seop/AnaliseDeProducao';
import ConfirmModal from '@/components/seop/ConfirmModal';
import { BarraDeCenarios, TabelaCenarios } from '@/components/seop/cenarios/CenariosSalvos';
import CorrigirMesDoCenario from '@/components/seop/cenarios/CorrigirMesDoCenario';
import FaixaDoPlano, { type RestauracaoRecente } from '@/components/seop/cenarios/FaixaDoPlano';
import PainelDoPeriodo from '@/components/seop/cenarios/PainelDoPeriodo';
import { useToast } from '@/contexts/ToastContext';
import { useSeopCenarioAberto } from '@/hooks/useSeopCenarioAberto';
import { useSeopMonth } from '@/hooks/useSeopMonth';
import { seopService } from '@/services/seop.service';
import type { SeopCalc, SeopMonth, SeopScenario } from '@/types/seop';
import { fmtMoneyBRL, fmtUnits, fmtUtil, seopNomeDoMes, seopNum, seopStatus } from '@/utils/seopFormat';
import { anoMes, mensagemDoMes } from '@/utils/seopMeses';
import {
  FILTRO_INICIAL,
  mesDoCenario,
  nomeDoBackupDeDesfazer,
  PREFIXO_BACKUP,
  ressalvasDaRestauracao,
  rotuloDoMes,
  sugerirNome,
  ultimoSalvo,
  type FiltroDoCenario,
} from '@/utils/seopCenario';

const CELL = 'py-2 pr-3 text-sm text-content-primary';
const NUM = `${CELL} text-right tabular-nums`;

/**
 * A comparação lado a lado dos cenários selecionados.
 *
 * Fora da página porque ela já carrega o painel do período, os cartões e os cenários
 * salvos — e o teto de complexidade do ESLint (10) é o sinal de que o bloco tem vida
 * própria.
 */
const TabelaComparacao: FC<{
  comparacao: Array<{ scenario: SeopScenario; calc: SeopCalc }>;
  melhor: string | null;
}> = ({ comparacao, melhor }) => {
  if (comparacao.length < 2) return null;
  return (
    <div
      data-testid="seop-comparacao"
      className="mt-4 rounded-xl border border-theme-border bg-surface-card p-5"
    >
      <h3 className="mb-3 font-semibold text-content-primary">Comparação</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-theme-border text-xs uppercase tracking-wide text-content-muted">
              <th className="pb-2 pr-3 text-left">Indicador</th>
              {comparacao.map((c) => (
                <th key={c.scenario.id} className="pb-2 pr-3 text-right">
                  {c.scenario.name}
                  {melhor === c.scenario.id && (
                    <span
                      data-testid="seop-melhor-cenario"
                      className="ml-2 rounded-full border border-theme-success/40 bg-theme-success/10 px-2 py-0.5 text-2xs text-theme-success"
                    >
                      Melhor cenário
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <LinhaComparacao
              rotulo="Unidades líquidas"
              valores={comparacao.map((c) => liquidoTotal(c.calc))}
              formatar={fmtUnits}
            />
            <LinhaComparacao
              rotulo="Receita"
              valores={comparacao.map((c) => receitaTotal(c.calc))}
              formatar={fmtMoneyBRL}
            />
            <LinhaComparacao
              rotulo="Margem"
              valores={comparacao.map((c) => margemTotal(c.calc))}
              formatar={fmtMoneyBRL}
            />
            <LinhaComparacao
              rotulo="Margem/hora de gargalo"
              valores={comparacao.map((c) => margemGargalo(c.calc))}
              formatar={fmtMoneyBRL}
            />
            {/* A tabela de custo de turno saiu da tela (pedido do Herrison, set/26).
                A linha aqui só aparece quando algum cenário tem custo de fato — com
                alavanca zerada ela era uma fileira de R$ 0,00 marcada como "melhor". */}
            {comparacao.some((c) => custoTotal(c.calc) > 0) && (
              <LinhaComparacao
                rotulo="Custo do regime de turno"
                valores={comparacao.map((c) => custoTotal(c.calc))}
                formatar={fmtMoneyBRL}
                menorEMelhor
              />
            )}
            <tr className="border-b border-theme-border/50">
              <td className={CELL}>Maior utilização</td>
              {comparacao.map((c) => {
                const u = seopNum(c.calc.totals.max_util);
                    return (
                  <td key={c.scenario.id} className={NUM}>
                    <span className={toneClass(seopStatus(u).tone)}>{fmtUtil(u)}</span>
                  </td>
                    );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * O que "tornar plano atual" faz, dito sem jargão. Espelha `scenario_restore` no backend:
 * voltam as DECISÕES do plano, só no mês do cenário; cadastro, OEE/dias/deduções da
 * capacidade e os outros meses ficam os de hoje. Mudou a regra lá, muda o texto aqui.
 */
const AvisoDeRestauracao: FC<{ mes: string }> = ({ mes }) => (
  <>
    <p data-testid="seop-restauracao-mes">
      <strong>Só {mes || 'o mês do cenário'} muda.</strong> Os outros meses do plano ficam como estão.
    </p>
    <p>
      <strong>O que volta, como estava no cenário:</strong> a demanda, o mix e as alocações, os turnos e a hora extra
      de cada máquina, a projeção e o custo de turno.
    </p>
    <p>
      <strong>O que não muda:</strong> o cadastro (produtos, roteiros, lotes, preços) e, na capacidade, o OEE, os dias e
      as deduções. Produto ou máquina criado depois do cenário fica como está.
    </p>
    <p>O plano atual é compartilhado: a mudança vale para toda a equipe.</p>
    <p>
      <strong>Antes de trocar, o sistema guarda o plano atual</strong> na lista de cenários — dá para desfazer logo
      depois, ou voltar a ele quando quiser.
    </p>
  </>
);

/** O que a tela mostra agora: o plano atual ou a foto do cenário aberto para leitura. */
function conteudoEmTela(
  aberto: SeopScenario | null,
  calcAberto: SeopCalc | null,
  calc: SeopCalc,
  months: SeopMonth[],
  mesLabel: string,
): { calc: SeopCalc | null; titulo: string; mesDoCenario: string; abertoId: string | null; semMes: boolean } {
  if (!aberto) {
    return { calc, titulo: 'Plano atual do período', mesDoCenario: mesLabel, abertoId: null, semMes: false };
  }
  return {
    calc: calcAberto,
    titulo: `Cenário salvo: ${aberto.name}`,
    mesDoCenario: mesDoCenario(aberto, months),
    abertoId: aberto.id,
    // O cenário só tem os meses que existiam quando foi salvo; o backend diz se o mês
    // do seletor é um deles.
    semMes: calcAberto?.scenario?.has_month === false,
  };
}

/** As duas confirmações da tela, fora da página para não estourar o teto de complexidade. */
const ModaisDeCenario: FC<{
  restaurando: SeopScenario | null;
  /** O mês do cenário que vai virar o plano atual — é o único que muda. */
  mesDoRestaurando: string;
  excluindo: SeopScenario | null;
  isBusy: boolean;
  nomeDoBackup: string;
  onNomeDoBackup: (v: string) => void;
  onRestaurar: () => void;
  onExcluir: () => void;
  onCancelar: () => void;
}> = ({
  restaurando,
  mesDoRestaurando,
  excluindo,
  isBusy,
  nomeDoBackup,
  onNomeDoBackup,
  onRestaurar,
  onExcluir,
  onCancelar,
}) => (
  <>
    <ConfirmModal
      open={restaurando !== null}
      title={`Tornar "${restaurando?.name ?? ''}" o plano atual?`}
      confirmLabel="Tornar plano atual"
      tone="accent"
      isBusy={isBusy}
      onConfirm={onRestaurar}
      onCancel={onCancelar}
    >
      <AvisoDeRestauracao mes={mesDoRestaurando} />
      <label className="mt-3 block text-xs text-content-muted">
        Guardar o plano atual como (opcional)
        <input
          aria-label="Nome para guardar o plano atual"
          value={nomeDoBackup}
          placeholder={`${PREFIXO_BACKUP} — antes de restaurar ${restaurando?.name ?? ''}`}
          onChange={(e) => onNomeDoBackup(e.target.value)}
          maxLength={200}
          className="mt-1 block w-full rounded-lg border border-theme-border bg-surface-secondary px-3 py-1.5 text-sm text-content-primary"
        />
        <span className="mt-1 block">Com nome, ele entra na lista como um cenário seu; sem nome, vai para os backups automáticos.</span>
      </label>
    </ConfirmModal>

    <ConfirmModal
      open={excluindo !== null}
      title={`Excluir o cenário "${excluindo?.name ?? ''}"?`}
      confirmLabel="Excluir"
      isBusy={isBusy}
      onConfirm={onExcluir}
      onCancel={onCancelar}
    >
      <p>O snapshot é apagado e não pode mais ser aberto, restaurado nem comparado.</p>
    </ConfirmModal>
  </>
);

const SeopScenariosPage: FC = () => {
  const { month, setMonth } = useSeopMonth();
  const { showToast } = useToast();
  const [calc, setCalc] = useState<SeopCalc | null>(null);
  const [scenarios, setScenarios] = useState<SeopScenario[]>([]);
  const [months, setMonths] = useState<SeopMonth[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [nome, setNome] = useState('');
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [comparacao, setComparacao] = useState<Array<{ scenario: SeopScenario; calc: SeopCalc }>>([]);
  const [restaurando, setRestaurando] = useState<SeopScenario | null>(null);
  const [excluindo, setExcluindo] = useState<SeopScenario | null>(null);
  const [movendo, setMovendo] = useState<SeopScenario | null>(null);
  const [filtro, setFiltro] = useState<FiltroDoCenario>(FILTRO_INICIAL);
  const [nomeDoBackup, setNomeDoBackup] = useState('');
  const [restauracao, setRestauracao] = useState<RestauracaoRecente | null>(null);
  const { aberto, calcAberto, abrir, fechar } = useSeopCenarioAberto(month);

  const mesLabel = rotuloDoMes(months, month);

  const fetchCalc = useCallback(async () => {
    setCalc(await seopService.getCalc(month));
  }, [month]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [, sc, ms] = await Promise.all([
        fetchCalc(),
        seopService.getScenarios(),
        seopService.getMonths(),
      ]);
      setScenarios(sc);
      setMonths(ms);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro desconhecido');
    } finally {
      setIsLoading(false);
    }
  }, [fetchCalc]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const salvarCenario = async () => {
    if (!nome.trim()) {
      showToast('Dê um nome ao cenário.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      await seopService.createScenario(nome.trim(), month);
      setNome('');
      showToast('Cenário salvo. O plano atual continua igual.', 'success');
      setScenarios(await seopService.getScenarios());
    } catch {
      showToast('Erro ao salvar o cenário.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const exportar = async () => {
    if (selecionados.length === 0) {
      showToast('Escolha ao menos um cenário para exportar.', 'error');
      return;
    }
    setIsExporting(true);
    try {
      await seopService.exportReport(selecionados, month);
    } catch (e) {
      const data = (e as { response?: { data?: { message?: string } } })?.response?.data;
      showToast(data?.message || 'Não consegui gerar o report.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const comparar = async () => {
    if (selecionados.length < 2) {
      showToast('Escolha ao menos dois cenários para comparar.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const resultados = await Promise.all(
        selecionados.map(async (id) => {
          const cenario = scenarios.find((s) => s.id === id) as SeopScenario;
          return { scenario: cenario, calc: await seopService.getCalcFromScenario(id, month) };
        }),
      );
      setComparacao(resultados);
    } catch {
      showToast('Erro ao comparar os cenários.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  /** O cenário é de um mês: abrir ou aplicar leva o seletor de período para lá. */
  const irParaOMes = (indice: number | null | undefined) => {
    if (typeof indice === 'number' && indice !== month) setMonth(indice);
  };

  const abrirNoMesDele = (s: SeopScenario) => {
    irParaOMes(s.month?.index);
    abrir(s);
  };

  const corrigirMes = async (mes: SeopMonth) => {
    if (!movendo) return;
    setIsSaving(true);
    try {
      await seopService.redateScenario(movendo.id, anoMes(mes.date));
      // A foto aberta e a comparação foram calculadas com o mês antigo do cenário.
      if (aberto?.id === movendo.id) fechar();
      setComparacao([]);
      setMovendo(null);
      setScenarios(await seopService.getScenarios());
      showToast(`"${movendo.name}" agora é um cenário de ${seopNomeDoMes(mes)}.`, 'success');
    } catch (e) {
      showToast(mensagemDoMes(e) || 'Não consegui corrigir o mês do cenário. Nada foi alterado.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const restaurar = async () => {
    if (!restaurando) return;
    setIsSaving(true);
    try {
      const resultado = await seopService.restoreScenario(restaurando.id, nomeDoBackup);
      setRestaurando(null);
      setNomeDoBackup('');
      // Depois de trocar, a tela volta para o plano atual, no mês que mudou.
      fechar();
      setRestauracao({
        nome: resultado.restored.name,
        mes: resultado.month?.name ?? '',
        backup: resultado.backup,
        ressalvas: ressalvasDaRestauracao(resultado.skipped),
      });
      showToast(`"${resultado.restored.name}" agora é o plano atual.`, 'success');
      irParaOMes(resultado.month?.index);
      await fetchData();
    } catch (e) {
      showToast(mensagemDoMes(e) || 'Erro ao tornar o cenário o plano atual. Nada foi alterado.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  /** Desfazer = restaurar o snapshot guardado antes da troca (que também guarda o de agora). */
  const desfazer = async () => {
    if (!restauracao) return;
    setIsSaving(true);
    try {
      await seopService.restoreScenario(restauracao.backup.id, nomeDoBackupDeDesfazer(restauracao.nome));
      setRestauracao(null);
      showToast('Desfeito: o plano atual voltou a ser o de antes.', 'success');
      await fetchData();
    } catch {
      showToast('Não consegui desfazer. O plano anterior continua guardado na lista de cenários.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const excluir = async () => {
    if (!excluindo) return;
    setIsSaving(true);
    try {
      await seopService.deleteScenario(excluindo.id);
      if (aberto?.id === excluindo.id) fechar();
      // Sem o snapshot guardado não há o que desfazer.
      setRestauracao((atual) => (atual?.backup.id === excluindo.id ? null : atual));
      setSelecionados((atual) => atual.filter((id) => id !== excluindo.id));
      setExcluindo(null);
      setScenarios(await seopService.getScenarios());
      showToast('Cenário excluído.', 'success');
    } catch {
      showToast('Erro ao excluir o cenário.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  /** Maior margem total vence — é o critério do "Melhor cenário". */
  const melhor = useMemo(() => {
    if (comparacao.length < 2) return null;
    return comparacao.reduce((a, b) =>
      margemTotal(b.calc) > margemTotal(a.calc) ? b : a,
    ).scenario.id;
  }, [comparacao]);

  if (isLoading) return <div className="p-6 lg:p-10 text-content-muted">Carregando os cenários…</div>;

  if (error || !calc) {
    return (
      <div className="p-6 lg:p-10">
        <div className="rounded-lg border border-theme-danger/40 bg-theme-danger/10 p-4 text-content-primary">
          Não foi possível carregar os cenários. {error}
        </div>
      </div>
    );
  }

  const tela = conteudoEmTela(aberto, calcAberto, calc, months, mesLabel);

  return (
    <div className="p-6 lg:p-10">
      <FaixaDoPlano
        aberto={aberto}
        ultimoSalvo={ultimoSalvo(scenarios)}
        mes={mesLabel}
        mesDoCenario={tela.mesDoCenario}
        semMes={tela.semMes}
        restauracao={restauracao}
        ocupado={isSaving}
        onVoltar={fechar}
        onRestaurar={() => setRestaurando(aberto)}
        onDesfazer={desfazer}
      />

      <PainelDoPeriodo
        calc={tela.calc}
        titulo={tela.titulo}
        mes={mesLabel}
        filtro={filtro}
        onFiltro={setFiltro}
      />

      <div className="mt-4 rounded-xl border border-theme-border bg-surface-card p-5">
        <h3 className="mb-3 font-semibold text-content-primary">Cenários salvos</h3>
        <BarraDeCenarios
          nome={nome}
          onNome={setNome}
          sugestao={sugerirNome(scenarios)}
          selecionados={selecionados.length}
          isSaving={isSaving}
          isExporting={isExporting}
          vendoCenario={aberto !== null}
          onSalvar={salvarCenario}
          onComparar={comparar}
          onExportar={exportar}
        />

        <TabelaCenarios
          scenarios={scenarios}
          months={months}
          selecionados={selecionados}
          abertoId={tela.abertoId}
          onToggle={(id, marcado) =>
            setSelecionados((atual) => (marcado ? [...atual, id] : atual.filter((x) => x !== id)))
          }
          onAbrir={abrirNoMesDele}
          onRestaurar={setRestaurando}
          onCorrigirMes={setMovendo}
          onExcluir={setExcluindo}
        />
      </div>

      <TabelaComparacao comparacao={comparacao} melhor={melhor} />

      {/* A análise compara o PLANO ATUAL com o realizado; com um cenário aberto ela
          falaria de outro conjunto de números que não o que está em tela. */}
      {!aberto && <AnaliseDeProducao month={month} />}

      <CorrigirMesDoCenario
        cenario={movendo}
        months={months}
        isBusy={isSaving}
        onConfirmar={corrigirMes}
        onCancelar={() => setMovendo(null)}
      />

      <ModaisDeCenario
        restaurando={restaurando}
        mesDoRestaurando={restaurando ? mesDoCenario(restaurando, months) : ''}
        excluindo={excluindo}
        isBusy={isSaving}
        nomeDoBackup={nomeDoBackup}
        onNomeDoBackup={setNomeDoBackup}
        onRestaurar={restaurar}
        onExcluir={excluir}
        onCancelar={() => {
          setRestaurando(null);
          setExcluindo(null);
          setNomeDoBackup('');
        }}
      />
    </div>
  );
};

const toneClass = (tone: 'success' | 'warning' | 'danger'): string =>
  tone === 'danger' ? 'text-theme-danger' : tone === 'warning' ? 'text-theme-warning' : 'text-theme-success';

const liquidoTotal = (c: SeopCalc): number =>
  c.economics.rows.reduce((s, r) => s + seopNum(r.net_qty), 0);
const receitaTotal = (c: SeopCalc): number =>
  c.economics.rows.reduce((s, r) => s + seopNum(r.final_value), 0);
const margemTotal = (c: SeopCalc): number =>
  c.economics.rows.reduce((s, r) => s + seopNum(r.contribution_value), 0);
const custoTotal = (c: SeopCalc): number => (c.costs ?? []).reduce((s, x) => s + seopNum(x.total), 0);
const margemGargalo = (c: SeopCalc): number => {
  const linhas = c.bottleneck?.margin_per_hour ?? [];
  return linhas.length ? seopNum(linhas[0].margin_per_hour) : 0;
};

const LinhaComparacao: FC<{
  rotulo: string;
  valores: number[];
  formatar: (v: number) => string;
  menorEMelhor?: boolean;
}> = ({ rotulo, valores, formatar, menorEMelhor = false }) => {
  const finitos = valores.filter((v) => Number.isFinite(v));
  const alvo = finitos.length ? (menorEMelhor ? Math.min(...finitos) : Math.max(...finitos)) : null;
  return (
    <tr className="border-b border-theme-border/50">
      <td className={CELL}>{rotulo}</td>
      {valores.map((v, i) => (
        <td key={i} className={NUM}>
          <span className={alvo !== null && v === alvo ? 'font-semibold text-theme-success' : ''}>
            {formatar(v)}
          </span>
        </td>
      ))}
    </tr>
  );
};

export default SeopScenariosPage;
